import { action, makeObservable, observable, runInAction } from 'mobx';

import _ from 'lodash';
import { apiClient } from '../clientUtils/apiClient';
import webViewStore from './webViewStore';
import { FirebaseAuthStore, TokenUserData } from './firebaseStore';
import { PUSH_TOPICS, PushTopic } from '../../types/pushTypes';
import modalStore from './modalStore';
import { BridgeResponse } from '../../types/bridgeTypes';
import { SignInProvider } from '../../types/authTypes';

export class FirebaseAppAuthStore implements FirebaseAuthStore {
    /**
     * 로그인 진행 중 상태
     */
    isLoggingIn = false;

    loginMethod: SignInProvider | null = null;

    me: TokenUserData | null = null;

    constructor(authReadyResolve: () => void) {
        makeObservable<FirebaseAppAuthStore, 'loginWithProvider'>(this, {
            isLoggingIn: observable,
            loginMethod: observable,
            me: observable,

            loginWithGoogle: action,
            loginWithApple: action,
            loginWithProvider: action,
            loginWithCode: action,
            getIdToken: action,
            updateMe: action,
            refreshToken: action,
            logout: action,
        });

        (async () => {
            await webViewStore.sendMessageToApp('waitUntilAuthReady');

            // setCustomUserClaims() 으로 별도로 지정한 정보는 getIdTokenResult() 로 가져와야 함
            const meResponse = await webViewStore.sendMessageToApp('getMe');

            runInAction(() => {
                if (meResponse.success) {
                    this.me = meResponse.result || null;
                }

                // 초기화는 UI 처리를 위해 지연 처리
                _.delay(() => {
                    authReadyResolve();
                }, 100);
            });
        })();
    }

    async loginWithGoogle() {
        await this.loginWithProvider('google');
    }

    async loginWithApple() {
        await this.loginWithProvider('apple');
    }

    private async loginWithProvider(loginMethod: 'google' | 'apple') {
        if (this.isLoggingIn) {
            return;
        }

        this.loginMethod = loginMethod;
        this.isLoggingIn = true;

        try {
            let loginResponse: BridgeResponse<'loginWithGoogle' | 'loginWithApple'>;

            if (loginMethod === 'google') {
                loginResponse = await webViewStore.sendMessageToApp('loginWithGoogle');
            } else if (loginMethod === 'apple') {
                loginResponse = await webViewStore.sendMessageToApp('loginWithApple');
            } else {
                throw new Error(`${loginMethod} login failed`);
            }

            // 앱에서는 인증 처리만 함
            // - 인증이 완료되면 웹과 동일하게 서버에 요청해서 사용자 정보를 갱신해줘야 함
            if (loginResponse.success && loginResponse.result) {
                const idToken = await this.getIdToken();

                if (idToken) {
                    const user = await apiClient.firebase.public.loginWithGoogleOrApple.mutate({
                        idToken,
                    });

                    if (user) {
                        // 로그인 후엔 서비스 정보도 업데이트 되었을 수 있으므로 토큰을 갱신해준다
                        await this.refreshToken();

                        // FCM 토큰을 서버에 전송 (앱에서 이미 보냈더라도 다시 보내도록 함)
                        await this.saveFcmTokenToServer(true);

                        // 다른 웹뷰에 로그인에 성공했다고 전파해준다
                        await webViewStore.publishToOtherWebViews('login-succeed');
                    } else {
                        throw new Error(`${loginMethod} login failed`);
                    }
                } else {
                    throw new Error(`${loginMethod} login failed`);
                }
            } else {
                throw new Error(`${loginMethod} login failed`);
            }
        } catch (e) {
            console.error(e);

            await modalStore.alert({
                title: '로그인 실패',
                message: '로그인 실패했습니다. 다시 시도해주세요.',
            });
        } finally {
            runInAction(() => {
                this.isLoggingIn = false;
                this.loginMethod = null;
            });
        }
    }

    async loginWithCode(code: string) {
        if (this.isLoggingIn) {
            return;
        }

        this.loginMethod = 'code';
        this.isLoggingIn = true;

        try {
            // 서버에서 코드 검증 후 Custom Token 획득
            const result = await apiClient.firebase.public.loginWithCode.mutate({ code });

            // 앱에 Custom Token 전달하여 로그인 처리
            const loginResponse = await webViewStore.sendMessageToApp('loginWithCustomToken', {
                customToken: result.customToken,
            });

            if (loginResponse.success) {
                // 로그인 후 토큰 갱신
                await this.refreshToken();

                // FCM 토큰을 서버에 전송
                await this.saveFcmTokenToServer(true);

                // 다른 웹뷰에 로그인에 성공했다고 전파
                await webViewStore.publishToOtherWebViews('login-succeed');
            }
        } finally {
            runInAction(() => {
                this.isLoggingIn = false;
                this.loginMethod = null;
            });
        }
    }

    async getIdToken(): Promise<string | null> {
        const idTokenResponse = await webViewStore.sendMessageToApp('getIdToken');
        if (idTokenResponse.success) {
            return idTokenResponse.result || null;
        }
        return null;
    }

    async updateMe() {
        const meResponse = await webViewStore.sendMessageToApp('getMe');

        runInAction(() => {
            if (meResponse.success) {
                this.me = meResponse.result || null;
            }
        });
    }

    async refreshToken() {
        await webViewStore.sendMessageToApp('refreshToken');

        const meResponse = await webViewStore.sendMessageToApp('getMe');
        runInAction(() => {
            if (meResponse.success) {
                this.me = meResponse.result || null;
            }
        });
    }

    async logout() {
        // 로그아웃 시 FCM 토큰 삭제
        await this.deleteFcmTokenFromServer();

        await webViewStore.sendMessageToApp('logout');
        runInAction(() => {
            this.me = null;
        });

        // 다른 웹뷰에 로그아웃했다고 전파해준다
        await webViewStore.publishToOtherWebViews('logout-succeed');
    }

    /**
     * FCM 토큰을 서버에 저장
     * - 로그인 상태일에 서버에 토큰 저장
     * - 로그인 상태이면 서버에서 내려준 구독 정보 업데이트
     * - 로그인 상태가 아니면 전체 사용자를 제외한 토픽 구독 해제
     */
    async saveFcmTokenToServer(force = false) {
        try {
            const fcmTokenResponse = await webViewStore.sendMessageToApp('getFcmTokenData');

            if (fcmTokenResponse.success && fcmTokenResponse.result) {
                const { deviceId, fcmToken, isTokenSentToWebView } = fcmTokenResponse.result;

                // 토큰이 이미 웹뷰로 전송되었다면 서버에 저장하지 않음
                if (!force && isTokenSentToWebView) {
                    return;
                }

                if (fcmToken && deviceId) {
                    try {
                        if (this.me) {
                            const response = await apiClient.firebase.member.saveFcmToken.mutate({
                                deviceId,
                                fcmToken,
                            });

                            // fcmToken 이 정상적으로 저장되었으면 앱의 토픽 구독 상태를 갱신
                            if (response?.topicsToSubscribe) {
                                await webViewStore.sendMessageToApp('setFcmTopics', {
                                    topicsToSubscribe: response.topicsToSubscribe,
                                    topicsToUnsubscribe: response.topicsToUnsubscribe,
                                });
                            }
                        } else {
                            // 로그인 상태가 아니면 전체 사용자를 제외한 토픽 구독 해제
                            await webViewStore.sendMessageToApp('setFcmTopics', {
                                topicsToSubscribe: ['all_devices'] as PushTopic[],
                                topicsToUnsubscribe: _.without(
                                    PUSH_TOPICS,
                                    'all_devices',
                                ) as PushTopic[],
                            });
                        }
                    } catch (error) {
                        // 실패한 경우 다시 시도할 수 있도록 앱에 실패처리 해줌
                        await webViewStore.sendMessageToApp('unmarkFcmTokenSentToWebView');
                    }
                } else {
                    // 제대로 저장되지 않았을 때 앱에 실패처리 해줌
                    await webViewStore.sendMessageToApp('unmarkFcmTokenSentToWebView');
                }
            }
        } catch (error) {
            console.error('FCM 토큰 저장 중 오류:', error);
        }
    }

    /**
     * FCM 토큰을 서버에서 삭제
     */
    private async deleteFcmTokenFromServer() {
        try {
            if (!this.me) {
                return;
            }

            const fcmTokenResponse = await webViewStore.sendMessageToApp('getFcmTokenData');

            if (fcmTokenResponse.success && fcmTokenResponse.result) {
                const { deviceId } = fcmTokenResponse.result;

                if (deviceId) {
                    await apiClient.firebase.member.deleteFcmToken.mutate({
                        deviceId,
                    });
                }

                // 로그아웃 시 전체 사용자를 제외한 토픽 구독 해제
                await webViewStore.sendMessageToApp('setFcmTopics', {
                    topicsToSubscribe: ['all_devices'] as PushTopic[],
                    topicsToUnsubscribe: _.without(PUSH_TOPICS, 'all_devices') as PushTopic[],
                });
            }
        } catch (error) {
            console.error('FCM 토큰 삭제 중 오류:', error);
        }
    }
}
